import { ParametersDetails } from "./PromptDetails/ParametersDetails";
import { TemplateDetails } from "./PromptDetails/TemplateDetails";
import { ToolDetails } from "./PromptDetails/ToolDetails";
import { PageSectionHeader } from "@components/library/PageSectionHeader";
import _ from "lodash";
import CodePreview from "../Code/CodePreview";
import {
  Evaluator,
  EvaluatorJudgmentOption,
  ExternalEvaluatorSpec,
  HumanEvaluatorRequest,
  returnTypeToString,
} from "@/types/app/evaluator";
import { ToastVariant, hlToast } from "@components/library/Toast";
import { DescriptionDetails, DescriptionList, DescriptionTerm } from "../DescriptionList";
import React from "react";
import { OptionNameTag } from "../Evaluations/EvaluatorEditor/EditorPanel/HumanEvaluatorEditor";
import { ValenceTag } from "../Evaluations/EvaluatorEditor/EditorPanel/ValenceTag";
import { classNames } from "@/lib/utils";
import { titleFromSnakeOrCamel } from "@/lib/string";
import { AttributeDetails } from "./AttributeDetails";

export const EvaluatorVersionDetails = ({ version }: { version: Evaluator }) => {
  return (
    <div>
      <TypedEvaluatorVersionDetails version={version} />
      <AttributeDetails {...version} />
    </div>
  );
};

export const TypedEvaluatorVersionDetails = ({ version }: { version: Evaluator }) => {
  switch (version.spec.evaluator_type) {
    case "llm":
      if (version.spec.prompt) {
        return (
          <div>
            <ParametersDetails {...version.spec.prompt} />
            <TemplateDetails
              template={version.spec.prompt.template}
              template_language={version.spec.prompt.template_language}
            />
            {/* We don't allow linking tools in LLM evaluators so empty array is all good. */}
            <ToolDetails tools={version.spec.prompt.tools || []} linked_tools={[]} />
          </div>
        );
      }
      console.error("No prompt found for LLM evaluator");
      hlToast({
        title: "No prompt found for LLM evaluator",
        variant: ToastVariant.Error,
      });
      return null;
    case "python":
      return (
        <div>
          <CodeDetails version={version} />
        </div>
      );
    case "external":
      return <ExternalEvaluatorDetails version={version} />;
    case "human":
      return (
        <div>
          <HumanEvaluatorDetails version={version} />
        </div>
      );
    default:
      const _exhaustiveCheck: never = version.spec;
      throw new Error(`Unknown evaluator type: ${version.spec}`);
  }
};

const CodeDetails = ({ version }: { version: Evaluator }) => {
  return (
    <div>
      <PageSectionHeader title={"Configuration"} size="sm" />
      <div className="flex w-full flex-col gap-20 text-13-14 text-text-base-4">
        <div className="flex gap-8">
          <div className="w-[168px] font-medium">Type</div>
          <div>{titleFromSnakeOrCamel(version.spec.arguments_type)}</div>
        </div>
        <div className="flex gap-8">
          <div className="w-[168px]">Returns</div>
          <div>{_.capitalize(version.spec.return_type)}</div>
        </div>
      </div>

      {"code" in version.spec && version.spec.code ? (
        <div className="mt-24 max-h-480 w-full min-w-[300px] overflow-auto rounded-ms bg-background-base-1 p-12 text-12-16 shadow-card-sm">
          <CodePreview showLineNumbers language="python">
            {version.spec.code || ""}
          </CodePreview>
        </div>
      ) : null}
    </div>
  );
};

export const EvaluatorOptionsTable = ({ options }: { options: EvaluatorJudgmentOption[] }) => {
  return (
    <div className="grid grid-cols-[auto_auto] overflow-hidden rounded-ms border border-stroke-base-3">
      <div className="flex h-36 items-center border-b border-stroke-base-3 pl-12">
        <div className="text-10-10 font-medium uppercase text-text-base-4">Option</div>
      </div>
      <div className="flex h-36 items-center border-b border-stroke-base-3 pl-12">
        <div className="text-10-10 font-medium uppercase text-text-base-4">Valence</div>
      </div>
      {options.map((option, index) => (
        <div key={option.name} className={classNames("contents", index % 2 === 1 && "*:bg-background-base-2")}>
          <div className="flex px-12 py-8">
            <OptionNameTag name={option.name} />
          </div>
          <div className="px-12 py-8">
            <ValenceTag valence={option.valence} />
          </div>
        </div>
      ))}
    </div>
  );
};

const HumanEvaluatorDetails = ({ version }: { version: Evaluator }) => {
  const spec: HumanEvaluatorRequest = version.spec as HumanEvaluatorRequest;
  return (
    <div>
      <PageSectionHeader title={"Configuration"} size="sm" />
      <DescriptionList>
        <DescriptionTerm>Type</DescriptionTerm>
        <DescriptionDetails>{returnTypeToString(spec.return_type)}</DescriptionDetails>
        {spec.options && (
          <>
            <DescriptionTerm>Options</DescriptionTerm>
            <DescriptionDetails>
              <EvaluatorOptionsTable options={spec.options} />
            </DescriptionDetails>
          </>
        )}
        {!_.isNil(spec.number_limits?.min) && (
          <>
            <DescriptionTerm>Min</DescriptionTerm>
            <DescriptionDetails>{spec.number_limits.min}</DescriptionDetails>
          </>
        )}
        {!_.isNil(spec.number_limits?.max) && (
          <>
            <DescriptionTerm>Max</DescriptionTerm>
            <DescriptionDetails>{spec.number_limits.max}</DescriptionDetails>
          </>
        )}
        {!_.isNil(spec.number_limits?.step) && (
          <>
            <DescriptionTerm>Step</DescriptionTerm>
            <DescriptionDetails>{spec.number_limits.max}</DescriptionDetails>
          </>
        )}
        {spec.number_valence && (
          <>
            <DescriptionTerm>Valence</DescriptionTerm>
            <DescriptionDetails>
              <ValenceTag valence={spec.number_valence} />
            </DescriptionDetails>
          </>
        )}
        <DescriptionTerm>Instructions</DescriptionTerm>
        <DescriptionDetails>{spec.instructions}</DescriptionDetails>
      </DescriptionList>
    </div>
  );
};

const ExternalEvaluatorDetails = ({ version }: { version: Evaluator }) => {
  const spec: ExternalEvaluatorSpec = version.spec as ExternalEvaluatorSpec;
  return (
    <div>
      <PageSectionHeader title={"Configuration"} size="sm" />
      <DescriptionList>
        <DescriptionTerm>Type</DescriptionTerm>
        <DescriptionDetails>{returnTypeToString(spec.return_type)}</DescriptionDetails>
      </DescriptionList>
    </div>
  );
};
